/**
 * MONEDA-VISTA-EN-REQUESTS.JS — inyección del selector de moneda en las lecturas.
 *
 * El panel tiene DOS capas de red independientes (`App.fetchWithAuth` con rutas
 * `/api/admin/...` y `AdminApiClient.callAdmin` con rutas `/admin/...` sobre una
 * base distinta). Ambas deben mandar `moneda_vista`, así que la lista de endpoints
 * y la regla de inyección viven acá y no duplicadas en cada capa.
 *
 * Contrato del param: SPEC_admin-moneda-de-vista.md §3.
 */
import { MonedaService } from './moneda-service.js';

/**
 * Endpoints de LECTURA que consolidan multi-moneda y aceptan `moneda_vista` como
 * destino (Fase A del backend). Se comparan contra el path SIN query string, y
 * sin el prefijo de base: se acepta tanto `/api/admin/x` como `/admin/x` porque
 * cada capa arma la URL a su manera.
 *
 * Es una lista explícita y no un "mandarlo siempre" a propósito: son 171 call
 * sites y la mayoría son escrituras o lecturas de una sola moneda. Un param que
 * el endpoint ignora es ruido inofensivo hasta que alguien agregue validación
 * estricta de query y empiece a devolver 400.
 */
export const ENDPOINTS_QUE_ACEPTAN_MONEDA_DE_VISTA = [
    /\/admin\/movimientos\/estadisticas$/,
    /\/admin\/tickets\/stats$/,
    /\/admin\/nodos\/\d+\/stats$/,
    /\/admin\/nodos\/\d+\/stats\/evolution$/,
    /\/admin\/nodos\/\d+\/saldo-operativo$/,
    /\/admin\/nodos\/saldos-operativos$/,
    /\/admin\/sorteos-kpis$/,
    /\/admin\/command-center$/,
    /\/admin\/pasarela\/transacciones-listado$/,
];

/**
 * Agrega `moneda_vista` a una URL de lectura que lo entienda, si el operador eligió
 * una moneda en el selector del panel. Devuelve la URL sin tocar en cualquier otro
 * caso.
 *
 * Solo GET: el param es de presentación, y una escritura que mueve dinero no debe
 * cambiar de comportamiento por lo que el usuario tenga elegido en un desplegable
 * de visualización.
 *
 * Si el caller ya puso `moneda_vista` explícito (finanzas.js lo hace para la
 * pestaña Pasarela), gana el caller: sabe algo que este helper no.
 *
 * @param {string} url - path con o sin query string.
 * @param {string} [metodo='GET']
 * @returns {string}
 */
export function conMonedaDeVista(url, metodo = 'GET') {
    if (typeof url !== 'string' || !url) return url;
    if (String(metodo).toUpperCase() !== 'GET') return url;
    const moneda = MonedaService.getMonedaVista();
    if (!moneda) return url;
    const [ruta, queryString = ''] = url.split('?');
    if (!ENDPOINTS_QUE_ACEPTAN_MONEDA_DE_VISTA.some(patron => patron.test(ruta))) return url;
    if (/(^|&)moneda_vista=/.test(queryString)) return url;
    return `${ruta}?${queryString ? queryString + '&' : ''}moneda_vista=${encodeURIComponent(moneda)}`;
}
